import React, { FunctionComponent, useRef, useState, useEffect } from "react"
import usePromise from "react-use-promise"
import { Storage } from "aws-amplify"
import { makeStyles, Theme } from "@material-ui/core"

/** Scoped @material-ui/core stylesheet */
const useStyles = makeStyles<Theme>(theme => ({
  image: {
    maxWidth: "100%",
    borderRadius: theme.shape.borderRadius,
  },
}))

/** RenderUploadedImage component props */
interface RenderUploadedImageProps {
  value: string | File | undefined
}

export const RenderUploadedImage: FunctionComponent<RenderUploadedImageProps> = ({
  value,
}) => {
  /** Get the inferred type */
  const type = getTypeFromValue(value)

  /** Get the image URL if there's one available */
  const ref = useRef<HTMLImageElement | null>(null)
  const [reader] = useState(new FileReader())

  /** When the value changes, use the reader to set the URL */
  useEffect(() => {
    if (value instanceof File) {
      // setup the file reader
      reader.onload = () => {
        if (ref.current && typeof reader.result === "string") {
          ref.current.src = reader.result
        }
      }

      // read the file
      reader.readAsDataURL(value)
    }
  }, [reader, value])

  /** Grab the S3 image if the value contains an S3 */
  const [s3Url] = usePromise(() => {
    if (typeof value === "string" && type === "s3") {
      return Storage.get(value.split("s3:")[1])
    } else {
      return Promise.resolve(undefined)
    }
  }, [value])

  /** Get scoped CSS classes */
  const classes = useStyles()

  switch (type) {
    case "s3":
      return typeof s3Url === "string" ? (
        <img src={s3Url} alt="Upload" className={classes.image} />
      ) : null
    case "url":
      return typeof value === "string" ? (
        <img src={value} alt="Upload" className={classes.image} />
      ) : null
    case "file":
      return <img ref={ref} alt="Upload" className={classes.image} />
    default:
      return null
  }
}

/**
 *
 * @param value the value to infer type of
 */
const getTypeFromValue = (value: string | File | undefined) => {
  if (value instanceof File) return "file"
  else if (typeof value === "string" && value.includes("s3:")) return "s3"
  else if (typeof value === "string" && value.includes("http")) return "url"

  return typeof value
}
